import React from 'react';
import './LOFAScheduleEditModeHeader.css';
import { CustomLofaButton } from '../../../_common/CustomLofaButton/CustomLofaButton';
import { useStoreContext } from '../../../App/StoreProvider';
import { useMutation } from '@apollo/client/react';
import { UPDATE_LOFA_SCHEDULE_INFO, ADD_FACILITY_TO_LOFA } from '../../../../graphql/client/mutations';
import { GET_LOFA_SCHEDULE, GET_JOB_PROFILE, GET_LOAD_SUMMARY } from '../../../../graphql/client/queries';
import {
    AddFacilityToLofa,
    GetLofaSchedule_getLofaSchedule as LOFAScheduleSummaryData,
    LofaDetailByProppantEdit,
} from '../../../../types/schemaTypes';
import { LofaScheduleSummaryObject } from '../../../../store/types/LofaScheduleEditState';
import LOFAScheduleEditMode from '../LOFAScheduleEditMode';
import ConfirmEditLofaSchedule from '../ConfirmEditLofaSchedule/ConfirmEditLofaSchedule';
import * as LOFAScheduleEditActions from '../../../../store/actions/lofaScheduleEdit';
import Alert from 'react-bootstrap/Alert';

interface LOFAScheduleEditHeaderModeProps {
    changeEditMode: () => void;
    jobId: number;
    lofaScheduleSummaryList: LOFAScheduleSummaryData[];
}

const LOFAScheduleEditModeHeader = ({
    changeEditMode,
    jobId,
    lofaScheduleSummaryList,
}: LOFAScheduleEditHeaderModeProps): React.ReactElement => {
    const [state, dispatch] = useStoreContext();
    const { lofaScheduleEdit } = state;
    const [updateLofaScheduleInfo] = useMutation(UPDATE_LOFA_SCHEDULE_INFO, {
        onCompleted: changeEditMode,
        refetchQueries: () => {
            return [
                {
                    query: GET_JOB_PROFILE,
                    variables: { jobId: jobId },
                },
                {
                    query: GET_LOFA_SCHEDULE,
                    variables: { jobId: jobId },
                },
                {
                    query: GET_LOAD_SUMMARY,
                    variables: { jobId: jobId },
                },
            ];
        },
    });

    const [addFacilityToLofa, { data }] = useMutation<AddFacilityToLofa>(ADD_FACILITY_TO_LOFA, {
        onCompleted: () => {
            if (!data) {
                changeEditMode();
            }
        },
        refetchQueries: () => {
            return [
                {
                    query: GET_JOB_PROFILE,
                    variables: { jobId: jobId },
                },
                {
                    query: GET_LOFA_SCHEDULE,
                    variables: { jobId: jobId },
                },
                {
                    query: GET_LOAD_SUMMARY,
                    variables: { jobId: jobId },
                },
            ];
        },
    });

    if ((data?.addFacilityToLofa?.errors?.length ?? 0) > 0) {
        return (
            <Alert variant="info" onClose={changeEditMode} dismissible>
                Proposed facility already exists!
            </Alert>
        );
    }

    const onClickCancel = (): void => {
        dispatch(LOFAScheduleEditActions.setInitialData(lofaScheduleSummaryList, []));
        changeEditMode();
    };

    const onClickSave = (): void => {
        if (lofaScheduleEdit.toBePosted !== null && lofaScheduleEdit.toBePosted !== undefined) {
            const newLofaScheduleValues = lofaScheduleEdit.toBePosted.map(element => {
                if (element != null && element.proppantId != null && element.proppantName != null) {
                    const newLofaDetail: LofaDetailByProppantEdit = {
                        jobSummaries: element.jobSummaries
                            .filter(j => j.lofaName != '')
                            .map(j => ({
                                jobSummaryId: j.jobSummaryId || 0,
                                jobId: jobId,
                                lofaId: j.lofaId || 0,
                                lofaName: j.lofaName,
                                totalVolume: j.totalVolume ? +j.totalVolume : undefined,
                                lofaVolume: j.lofaVolume ? +j.lofaVolume : undefined,
                                volumePerBox: j.volumePerBox ? +j.volumePerBox : undefined,
                                proppantId: +element.proppantId,
                                milesFromWell: j.milesFromWell ? +j.milesFromWell : undefined,
                                sandPo: j.sandPo,
                                transportationPo: j.transportationPo,
                                isDeleted: j.isDeleted,
                            })),
                        proppantId: +element.proppantId,
                        proppantName: element.proppantName,
                    };
                    return newLofaDetail;
                }
            });
            let isNew = false;
            const variables = {
                input: {
                    jobId: jobId,
                    newValues: newLofaScheduleValues,
                },
            };
            newLofaScheduleValues.map(it => {
                if (it && it.proppantId != null && it.proppantName != null) {
                    it.jobSummaries &&
                        it.jobSummaries.map(j => {
                            if (j && j.jobSummaryId === 0) {
                                isNew = true;
                            }
                        });
                }
            });
            if (isNew) {
                addFacilityToLofa({
                    variables,
                }).then(() => {
                    dispatch(LOFAScheduleEditActions.setInitialData(lofaScheduleSummaryList, []));
                    dispatch(LOFAScheduleEditActions.setLofaScheduleToInitialState());
                });
            } else {
                updateLofaScheduleInfo({
                    variables,
                }).then(() => {
                    dispatch(LOFAScheduleEditActions.setInitialData(lofaScheduleSummaryList, []));
                    dispatch(LOFAScheduleEditActions.setLofaScheduleToInitialState());
                });
            }
        }
    };

    return (
        <div className="LOFA-Container">
            <div className="LOFASchedule-EditHeader">
                <div className="LOFASchedule-HeaderLeft">
                    <i className="material-icons LOFASchedule-EditIcon">create</i>
                    <span className="LOFASchedule-EditLink">Edit</span>
                    <span className="LOFASchedule-TitleText">LOFA Schedule</span>
                    <span className="LOFASchedule-TitleDivider">|</span>
                    <span className="LOFASchedule-HelpText">You must fill in all of the fields</span>
                </div>
                <div className="LOFASchedule-HeaderRight">
                    <div className="LOFASchedule-ButtonWrapper">
                        <CustomLofaButton isBordered={true} title="Cancel" onClick={onClickCancel} disabled={false} />
                    </div>
                    <div className="LOFASchedule-ButtonWrapper">
                        <ConfirmEditLofaSchedule
                            lofaScheduleSummaryList={lofaScheduleSummaryList as LofaScheduleSummaryObject[]}
                            onEditSave={onClickSave}
                        />
                    </div>
                </div>
            </div>
            <hr />
            <LOFAScheduleEditMode lofaScheduleSummaryList={lofaScheduleSummaryList} />
        </div>
    );
};

export default LOFAScheduleEditModeHeader;

